"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const KNOWN_ROUTES = [
  "/",
  "/cases",
  "/case-battle",
  "/roulette",
  "/crash",
  "/mines",
  "/keno",
  "/royale",
  "/market/cs2",
  "/sportsbook",
  "/sportsbook/counter-strike-109",
  "/rewards",
  "/giveaways",
  "/leaderboard",
  "/about",
  "/blog",
  "/help",
  "/provably-fair",
  "/terms-and-conditions",
];

const LEAVE_MS = 130;

/**
 * Vite-style SPA navigation for the verbatim clone.
 * Captured markup uses plain <a href> tags. We intercept internal clicks
 * and push them through Next's client router — no document reload, no
 * white flash — with a quick fade + top progress bar.
 */
export function SpaNav() {
  const router = useRouter();
  const pathname = usePathname();
  const [navigating, setNavigating] = useState(false);
  const pending = useRef<string | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Finish the transition every time the route changes.
  useEffect(() => {
    pending.current = null;
    setNavigating(false);
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    if (safetyTimer.current) {
      clearTimeout(safetyTimer.current);
      safetyTimer.current = null;
    }
    const root = document.getElementById("verbatim-root");
    if (root) {
      root.classList.remove("spa-leaving");
      root.classList.remove("spa-entering");
      void root.offsetWidth;
      root.classList.add("spa-entering");
      if (enterTimer.current) clearTimeout(enterTimer.current);
      enterTimer.current = setTimeout(() => {
        root.classList.remove("spa-entering");
      }, 380);
    }
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    return () => {
      if (enterTimer.current) clearTimeout(enterTimer.current);
    };
  }, [pathname]);

  // Warm the router cache on idle so clicks feel Vite-instant.
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      for (const r of KNOWN_ROUTES) {
        try {
          router.prefetch(r);
        } catch {
          /* noop */
        }
      }
    };
    const t = setTimeout(warm, 1200);
    const onIdle = () => warm();
    window.addEventListener("load", onIdle, { once: true });
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener("load", onIdle);
    };
  }, [router]);

  // Intercept internal <a> clicks → client-side push (no full reload).
  useEffect(() => {
    const normalize = (href: string): string | null => {
      try {
        // relative app link
        if (href.startsWith("/") && !href.startsWith("//")) {
          if (href.startsWith("/cdn-next") || href.startsWith("/_next")) return null;
          return href;
        }
        // absolute same-origin link (captured HTML sometimes has these)
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        const p = url.pathname + url.search + url.hash;
        if (p.startsWith("/cdn-next") || p.startsWith("/_next")) return null;
        return p;
      } catch {
        return null;
      }
    };

    const go = (href: string) => {
      if (pending.current === href) return;
      pending.current = href;
      const root = document.getElementById("verbatim-root");
      root?.classList.add("spa-leaving");
      setNavigating(true);
      // Safety: never leave the bar stuck on a failed navigation.
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
      safetyTimer.current = setTimeout(() => {
        setNavigating(false);
        pending.current = null;
        root?.classList.remove("spa-leaving");
      }, 4000);
      // Let the 130ms fade-out play before swapping the route —
      // this is what makes it feel like Vite instead of a hard redirect.
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(() => {
        try {
          const vt = (
            document as Document & {
              startViewTransition?: (cb: () => void) => void;
            }
          ).startViewTransition;
          if (vt) vt(() => router.push(href, { scroll: false }));
          else router.push(href, { scroll: false });
        } catch {
          router.push(href);
        }
      }, LEAVE_MS);
    };

    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      const anchor = el?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const raw = anchor.getAttribute("href");
      if (!raw) return;
      if (anchor.getAttribute("target") === "_blank") return;
      if (anchor.hasAttribute("download")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;

      const href = normalize(raw);
      if (!href) return;

      const current =
        window.location.pathname + window.location.search + window.location.hash;
      // Same-page hash → native handling.
      try {
        const url = new URL(href, window.location.origin);
        const here = window.location.pathname + window.location.search;
        if (url.pathname + url.search === here && url.hash) return;
      } catch {
        return;
      }
      // Same URL → swallow, never reload.
      if (href === current) {
        e.preventDefault();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      go(href);
    };

    // Prefetch on hover / touch so the next page is already cached.
    const prefetched = new Set<string>(KNOWN_ROUTES);
    const onHover = (e: Event) => {
      const el = e.target as HTMLElement | null;
      const anchor = el?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const raw = anchor.getAttribute("href");
      if (!raw) return;
      const href = normalize(raw);
      if (!href || prefetched.has(href)) return;
      prefetched.add(href);
      try {
        router.prefetch(href);
      } catch {
        /* noop */
      }
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("mouseover", onHover, true);
    document.addEventListener("focusin", onHover, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("mouseover", onHover, true);
      document.removeEventListener("focusin", onHover, true);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
    };
  }, [router]);

  return (
    <div
      className={`spa-progress${navigating ? " spa-progress-on" : ""}`}
      aria-hidden="true"
    >
      <div className="spa-progress-bar" />
    </div>
  );
}
